import HotelStatusIcon from 'components/HotelStatusIcon'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import ArrowDown from '../../../../components/ReusableComponents/Icons/ArrowDown'
import ArrowUp from '../../../../components/ReusableComponents/Icons/ArrowUp'
import DotIcon from '../../../../components/ReusableComponents/Icons/Dot'
import { Cell, Row, Spacer } from '../../../../components/ReusableComponents/Table'
import HotelIdCell from '../../Common/HotelIdCell'
import HotelNameCell from '../../Common/HotelNameCell/index'
import NoteCell from '../../Common/NoteCell'
import useNote from '../../Common/useNote'
import { useNoteBatchLoader } from '../../Common/NoteBatchLoaderContext'
import CheckCell from '../CheckCell'
import styles from './Row.module.scss'
import CustomTooltip from './Tooltip'

const NumberStatCell = ({ value, ...props }) => {
  const [isHovering, setHovering] = useState()

  return (
    <Cell className={styles.numberStatCell} {...props}>
      <div onMouseOver={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
        {value?.isIncreasing === true && <ArrowUp />}
        {value?.isIncreasing === false && <ArrowDown />}
        {value?.isIncreasing == null && <DotIcon />}

        {value?.periodCurrent}
        <CustomTooltip {...value} isVisible={isHovering} />
      </div>
    </Cell>
  )
}

const GoToAnalyticsButton = ({ subsidiaryId }) => {
  return (
    <Link to={`/s/${subsidiaryId}/analytics`}>
      <button>Go to Reports overview →</button>
    </Link>
  )
}

const AutoInviteHealthRow = ({
  showTelcoDetails,
  brand,
  displayId,
  invited,
  clicks,
  textInbound,
  textOutbound,
  segmentInbound,
  segmentOutbound,
  optOut,
  inviteFailedSpam,
  inviteFailedOther,
  bookings,
  conversions,
  shortName,
  marketingCampaigns,
  subsidiaryId,
  index,
  subsidiaryStatus,
  rowStatus,
  statusExplanation,
  csmUserFullName
}) => {
  const { registerCallback, notesCache } = useNoteBatchLoader()
  const {
    note,
    setNote,
    saveImmediately,
    isLoading: isLoadingNote,
    isSaving,
    error: noteError,
    loadNoteOnDemand,
    setNoteFromBatch
  } = useNote(subsidiaryId, 'healthCheck-autoinvite', { batchLoad: true })

  // Register callback to receive batch-loaded notes
  useEffect(() => {
    const key = `${subsidiaryId}-healthCheck-autoinvite`
    
    // Check if note is already in cache
    if (notesCache.has(key)) {
      setNoteFromBatch(notesCache.get(key))
    }

    // Register callback for future updates
    const unregister = registerCallback(subsidiaryId, 'healthCheck-autoinvite', (data) => {
      setNoteFromBatch(data)
    })

    return unregister
  }, [subsidiaryId, registerCallback, notesCache, setNoteFromBatch])

  return (
    <div
      className={`${styles.rowHolder} ${rowStatus ? styles[rowStatus] : ''}`}
      title={statusExplanation}
    >
      <div className={styles.index}>{index}</div>
      <div className={styles.statusIcon}>
        <HotelStatusIcon status={subsidiaryStatus} />
      </div>
      {showTelcoDetails ? (
        <Row>
          <HotelIdCell displayId={displayId} />
          <HotelNameCell
            brand={brand}
            name={shortName}
            navigationControl={<GoToAnalyticsButton subsidiaryId={subsidiaryId} />}
          />
          <Cell className={styles.textCell}>
            <span title={csmUserFullName}>
              {csmUserFullName
                ?.split(' ')
                .map(str => str[0])
                .join('.')}
            </span>
          </Cell>
          <NumberStatCell value={clicks} />
          <NumberStatCell value={invited} />
          <NumberStatCell value={bookings} />
          <NumberStatCell value={conversions} />
          <NumberStatCell value={textInbound} />
          <NumberStatCell value={textOutbound} />
          <NumberStatCell value={optOut} />
          <NumberStatCell value={segmentInbound} />
          <NumberStatCell value={segmentOutbound} />
          <NumberStatCell value={inviteFailedSpam} />
          <NumberStatCell value={inviteFailedOther} />
          <NoteCell
            note={note}
            setNote={setNote}
            saveImmediately={saveImmediately}
            loadNoteOnDemand={loadNoteOnDemand}
            isLoading={isLoadingNote}
            isSaving={isSaving}
            error={noteError}
          />
        </Row>
      ) : (
        <Row>
          <HotelIdCell displayId={displayId} />
          <HotelNameCell
            brand={brand}
            name={shortName}
            navigationControl={<GoToAnalyticsButton subsidiaryId={subsidiaryId} />}
          />
          <Cell className={styles.textCell}>
            <span title={csmUserFullName}>
              {csmUserFullName
                ?.split(' ')
                .map(str => str[0])
                .join('.')}
            </span>
          </Cell>
          <NumberStatCell value={clicks} />
          <NumberStatCell value={invited} />
          <NumberStatCell value={bookings} />
          <NumberStatCell value={conversions} />
          <Spacer />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'Lost3Months'
            )}
          />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'Lost6Months'
            )}
          />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'Lost9Months'
            )}
          />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'Lost12Months'
            )}
          />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'Lost15Months'
            )}
          />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'Lost15MonthsPlus'
            )}
          />
          <CheckCell
            campaign={marketingCampaigns?.find(
              campaign => campaign.category === 'AfterLastVisit'
            )}
          />
          <NoteCell
            note={note}
            setNote={setNote}
            saveImmediately={saveImmediately}
            loadNoteOnDemand={loadNoteOnDemand}
            isLoading={isLoadingNote}
            isSaving={isSaving}
            error={noteError}
          />
        </Row>
      )}
    </div>
  )
}

export default AutoInviteHealthRow
